import type { GridCard } from '@Components/BigBrowse/Model/Classes/GridCard';
import type { PlaneTypes } from '@Components/BigBrowse/Model/ModelTypes';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { useBbContentPresenter } from '../../BbContentPresenter';
import { MerchandiserCentralImage } from './Components/MerchandiserCentralImage';
import { MerchandiserContent } from './Components/MerchandiserContent';
import { MerchandiserContentWrapper } from './Components/MerchandiserContentWrapper';
import { MerchandiserExploreLabel } from './Components/MerchandiserExploreLabel';
import { MerchandiserProduct } from './Components/MerchandiserProduct';

const SHOW_EXPLORE_LABEL = false;

type Props = {
  gridCard: GridCard<PlaneTypes.Layout_Merchandiser>;
};

export const Merchandiser = observer(function Merchandiser({
  gridCard,
}: Props) {
  const {
    canvasManager: { cameraBoundaries },
  } = useBbContentPresenter();

  const data = gridCard.data!;
  const { internals } = data;

  const animatedIn = useIsAnimatedIn({
    gridCard,
    cameraTop: cameraBoundaries.top,
    cameraBottom: cameraBoundaries.bottom,
  });

  if (!internals) {
    return null;
  }

  return (
    <MerchandiserContentWrapper gridCard={gridCard}>
      <MerchandiserContent gridCard={gridCard}>
        <MerchandiserCentralImage
          gridCard={gridCard}
          animatedIn={animatedIn}
          zRange={[0, 0.1]}
        />

        {internals.productImages.map((_, index) => {
          const zMin = 0.1 + index * 0.01;
          const zMax = zMin + 0.01;

          return (
            <MerchandiserProduct
              key={index + gridCard.key!}
              gridCard={gridCard}
              index={index}
              animatedIn={animatedIn}
              zRange={[zMin, zMax]} // allowed zPositions for internal objects
            />
          );
        })}
        {SHOW_EXPLORE_LABEL && (
          <MerchandiserExploreLabel
            gridCard={gridCard}
            animatedIn={animatedIn}
            zRange={[0.2, 0.3]}
          />
        )}
      </MerchandiserContent>
    </MerchandiserContentWrapper>
  );
});

const useIsAnimatedIn = ({
  gridCard,
  cameraTop,
  cameraBottom,
}: {
  gridCard: GridCard<PlaneTypes.Layout_Merchandiser>;
  cameraTop: number;
  cameraBottom: number;
}): boolean => {
  const { data } = gridCard;
  const { internals } = data!;

  const [didAnimate, setDidAnimate] = useState(false);
  if (didAnimate) {
    return true;
  } // Make sure that Animates in only once

  if (!internals) {
    return false;
  }

  const { boxSize } = internals;
  const worldPosition = {
    x: 0, // center of the screen
    y: gridCard.worldPosition.y, // same level as the placeholder card
  };
  const [boxTop, boxBottom] = [
    worldPosition.y + boxSize.height / 2,
    worldPosition.y - boxSize.height / 2,
  ];

  const [top, bottom] = [
    Math.min(boxTop, cameraTop),
    Math.max(boxBottom, cameraBottom),
  ];

  const inScreenHeight = top - bottom;
  const isFullInScreen = inScreenHeight >= boxSize.height * (9 / 10);

  if (isFullInScreen) {
    setDidAnimate(true);
  }

  return isFullInScreen;
};
